import { useQuery } from "@tanstack/react-query";
import type { Environment } from "../../../types";
import type { LpBalanceInfo, LpBalanceReaderFn } from "../types";
import { readLpBalancesViaRpc } from "./lp-balance-rpc-fallback";

export const GAME_SWAP_LP_BALANCES_QUERY_KEY = "wp:game-swap-lp-balances";

/**
 * pair(LP) 주소들의 온체인 잔고/총발행량을 조회한다.
 * 주입형 `lpBalanceReader`가 있으면 그것을 쓰고, 없으면 CROSS 체인 RPC로
 * 직접 읽는 내부 폴백(`readLpBalancesViaRpc`)을 사용한다. 폴백마저 실패하면
 * 빈 맵을 돌려 섹션이 풀 정보만 표시하도록 한다.
 *
 * queryKey에 walletAddress를 포함해 계정 전환 시 자동 갱신되게 한다.
 * (reader는 함수라 직렬화되지 않으므로 주입 여부 boolean만 key에 넣는다.)
 */
export function useGameSwapLpBalances(
  env: Environment,
  walletAddress: string,
  pairAddresses: string[],
  reader: LpBalanceReaderFn | undefined,
) {
  const sortedPairs = [...pairAddresses]
    .map((a) => a.toLowerCase())
    .sort();
  const isEnabled = !!walletAddress && sortedPairs.length > 0;

  const { data, ...rest } = useQuery<Record<string, LpBalanceInfo>>({
    queryKey: [
      GAME_SWAP_LP_BALANCES_QUERY_KEY,
      env,
      walletAddress,
      sortedPairs,
      !!reader,
    ],
    queryFn: async ({ signal }) => {
      const result = reader
        ? await reader(sortedPairs)
        : await readLpBalancesViaRpc(env, walletAddress, sortedPairs, signal);
      // key를 소문자로 정규화해 조회 일관성 보장.
      const normalized: Record<string, LpBalanceInfo> = {};
      for (const [addr, info] of Object.entries(result)) {
        normalized[addr.toLowerCase()] = info;
      }
      return normalized;
    },
    enabled: isEnabled,
    staleTime: 0,
  });

  return { balances: data ?? {}, ...rest };
}
